// ── participants-helpers: admin-module access + scope + display + write errors ─
// Wave 4 (Participants). Shared by the participants list + detail orchestrators.
// Ids are STRINGS (bigint-safe) — passed VERBATIM, never Number()'d.
//
// PARTICIPANT ISOLATION (blueprint §6 gate #1, FR-PDASH-001): the ADMIN
// participants module (/participants + /participants/:id) is for STAFF / coach /
// company_admin / cohort_lead ONLY. A `participant` is DENIED the module
// entirely — they never see a roster or another participant's record here. Their
// own profile context is the Wave-9 Participant Dashboard, never this module.
// This is a fail-closed allowlist: an unknown/new role is denied until it is
// explicitly added.

import { humanizeLabel } from "@vjal/ui";

import { ApiClientError } from "../../services/api-client";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError
} from "../../services/coaching-error-posture";
import { isCutoffExceededError } from "./bulk-import/coach-cutoff";
import type { CoachingRole } from "../../domain/roles";

// ── Admin participants MODULE access (fail-closed allowlist) ─────────────────
// The route manifest already excludes `participant` from /participants
// (operationsRoles). This allowlist is the page-level fail-closed guard so the
// module stays denied for participant even if the route ever widened, AND it
// adds cohort_lead (assigned-cohort READ) which the module supports as a scoped
// reader. The server pins each role's scope; the frontend cannot widen it.
export const PARTICIPANTS_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

/** Whether the role may load the ADMIN participants module (fail-closed). */
export function canAccessParticipantsModule(role: CoachingRole): boolean {
  return PARTICIPANTS_MODULE_ROLES.includes(role);
}

/** A non-empty, non-"new" route segment is the only thing that can be a real id. */
export function isValidParticipantId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

export function participantDisplayName(
  name: string | null | undefined,
  email?: string | null | undefined
): string {
  const trimmedName = (name ?? "").trim();
  if (trimmedName.length > 0) return trimmedName;
  const trimmedEmail = (email ?? "").trim();
  return trimmedEmail.length > 0 ? trimmedEmail : "Unnamed participant";
}

export function participantStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "active");
}

export function participantStatusTone(status: string | null | undefined): "success" | "warning" | "danger" | "default" {
  switch (status) {
    case "active":
      return "success";
    case "removed":
    case "deactivated":
      return "danger";
    case "invited":
    case "pending":
      return "warning";
    default:
      return "default";
  }
}

/** Removed from their cohort: listed for the record only; Edit and Remove no longer apply. */
export function isRemovedParticipant(participant: { status?: string | null }): boolean {
  return participant.status === "removed";
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

// ── Participant status sets ──────────────────────────────────────────────────
// The editable set matches the statuses accepted by the participant PATCH
// validator. Removed is read-only directory history, so it is available to
// list filters but never offered by the edit form or cohort roster filter.
export const PARTICIPANT_STATUSES = ["active", "invited", "inactive", "completed"] as const;
export type ParticipantStatus = (typeof PARTICIPANT_STATUSES)[number];
export const PARTICIPANT_LIST_STATUSES = [...PARTICIPANT_STATUSES, "removed"] as const;

// ── Honest write-error → message + tone (mirrors companies-write) ───────────
export interface WriteErrorView {
  tone: "error" | "warning";
  message: string;
}

/**
 * Map a participant write error to an honest, non-leaking message. A closed gate
 * states nothing changed (no false success); a denied write reads as its honest
 * message. The single CO_* → posture map is reused so the modals speak the exact
 * same vocabulary as the list/detail reads.
 */
export function describeParticipantWriteError(error: unknown): WriteErrorView | null {
  if (error == null) return null;
  if (!(error instanceof ApiClientError)) {
    return { tone: "error", message: "Couldn't save the participant. Please try again." };
  }
  // The coach cutoff is an authoritative 410 domain refusal, not an outage.
  // Reuse the shared detector so create and bulk-import surfaces tell the same
  // truth and never turn a deliberate refusal into generic unavailable copy.
  if (isCutoffExceededError(error)) {
    return {
      tone: "warning",
      message: `${error.message || "The coach add window has closed."} No change was made.`
    };
  }
  const posture = postureForCoachingError(error);
  const reference = referenceFromError(error);
  const ref = reference ? ` (reference: ${reference})` : "";

  if (posture === "permission-denied") {
    return { tone: "error", message: "Your role can't make this change. Ask a super admin if you need it." };
  }
  if (posture === "gate-closed") {
    const gate = honestGateFromError(error);
    return {
      tone: "warning",
      message: `${gate?.reason ?? "This action is closed right now."} No change was made.`
    };
  }
  if (posture === "unavailable") {
    return { tone: "error", message: "Coaching is temporarily unavailable. Try again in a moment." };
  }
  // validation / not-found / generic — show the server message + reference.
  return { tone: "error", message: `${error.message}${ref}` };
}
